import PropTypes from "prop-types";
import React, { Fragment, useEffect, useState } from "react";
import { withRouter } from "react-router";
import { Button, Loader, Card, CardSection } from "cxa-components";
import RenderField from "cxa-components/field";
import { CheckboxField, StringField } from "cxa-form-field";
import { reduxForm } from "redux-form";
import { FormattedCustomDate } from "components/common/FormattedComponents";
import { injectIntl, intlShape } from "react-intl";
import messages from "components/messagesDescription";
import ClaimStepper from "./ClaimStepper";
import { useDispatch, useSelector } from "react-redux";
import { getClaimDetail } from "../../actions/claimActionsPP";
import ClaimRHS from "./ClaimRHS";
import { ReceiptPreviewer } from "./ReceiptPreviewField";
import { Referral } from "./ReferralFieldPP";
import { Document } from "./DocumentField";
import { renderAsHTML } from "utils/html";
import { submitClaim } from "../../actions/claimActionsPP";
import { getDashboardActions } from "../../actions/dashboardActionsPP";
import { Prompt } from "react-router-dom";
import StandardMoney from "../common/StandardMoney";
import { addDays } from "./util";

const formatValue = (value, type, item, field, intl) => {
  switch (type) {
    case "Date":
      return <FormattedCustomDate value={value} />;
    case "List":
    case "Autocomplete": {
      if (field.name === "provider" && value === "Others") {
        return renderAsHTML("span", item.providerName + ", " + item.postalCode);
      } else if (field.name === "provider" && value === "Others-Overseas") {
        return renderAsHTML("span", item.providerName);
      }
      const res = field.items.find(entry => entry.value === value);
      return renderAsHTML(
        "span",
        (res && intl.formatMessage({ id: res.label, defaultMessage: res.label })) ||
          intl.formatMessage({ id: value, defaultMessage: value })
      );
    }
    case "Decimal":
      if (field.name === "foreignReceiptAmount" || !item.displayCurrency) {
        return <StandardMoney value={value} />;
      }
      return <StandardMoney value={value} currency={item.currencyType} />;
    case "TextArea":
    case "String":
      return renderAsHTML("span", intl.formatMessage({ id: value, defaultMessage: value }));
    case "Boolean":
      if (field.name === "taxStatus") {
        return value
          ? intl.formatMessage(messages.taxableDescription)
          : intl.formatMessage(messages.nonTaxableDescription);
      }
      return value
        ? intl.formatMessage(messages.yesTextDescription)
        : intl.formatMessage(messages.noTextDescription);
    default:
      return value;
  }
};

const renderOutput = ({ id, value }) => (
  <div style={{ padding: "5px" }} id={`${id}`}>
    {value}
  </div>
);
renderOutput.propTypes = {
  id: PropTypes.string,
  value: PropTypes.any,
};

const Attachments = props => {
  const { attachments, intl } = props;
  const receiptFiles =
    (Array.isArray(attachments) && attachments.filter(file => file.field == "Receipt")) || [];
  const referralFiles =
    (Array.isArray(attachments) && attachments.filter(file => file.field == "Referral")) || [];
  const documentFiles =
    (Array.isArray(attachments) &&
      attachments.filter(file => file.field == "SupportingDocuments")) ||
    [];
  return (
    <div className="claim-preview-img">
      {receiptFiles.length > 0 && (
        <div className="section-divider">
          <CardSection>
            <span>
              <strong>{intl.formatMessage(messages.claims.receipts)}</strong>
            </span>
            <ReceiptPreviewer {...props} value={receiptFiles} allowDelete={false} />
          </CardSection>
        </div>
      )}
      {referralFiles.length > 0 && (
        <div className="section-divider">
          <CardSection>
            <span>
              <strong>{intl.formatMessage(messages.claims.referralDocument)}</strong>
            </span>
            {referralFiles.map(file => (
              <Referral {...props} key={file.objectId} referral={file} allowDelete={false} />
            ))}
          </CardSection>
        </div>
      )}
      {documentFiles.length > 0 && (
        <div className="section-divider">
          <CardSection>
            <span>
              <strong> {intl.formatMessage(messages.claims.supportingDocuments)}</strong>
            </span>
            {documentFiles.map(file => (
              <Document {...props} key={file.objectId} document={file} allowDelete={false} />
            ))}
          </CardSection>
        </div>
      )}
    </div>
  );
};

Attachments.propTypes = {
  attachments: PropTypes.array.isRequired,
};

const validateCheckRequired = (value, values, { intl: { formatMessage } }) =>
  value ? undefined : formatMessage(messages.requiredDescription);

const PreviewClaimForm = props => {
  const {
    match: { params },
    intl,
    push,
    handleSubmit,
    submitting,
    claimDetail,
    claimItem,
    submitSucceeded,
  } = props;
  const submittedClaim = useSelector(state => state.claimsPP.submittedClaim);
  const dispatch = useDispatch();
  const [dirty, setDirty] = useState(true);
  const clientSettings = useSelector(state => state.common.clientSettings);
  const numberOfDaysExpired = useSelector(
    state => state.common.clientSettings.draftClaimExpiresAfterNoDays
  );
  const goToPrevious = () => {
    setDirty(false);
    setTimeout(() => {
      push(
        params.selectedDelegator
          ? `/claims/edit/${params.claimId}/${params.selectedDelegator}`
          : `/claims/edit/${params.claimId}`
      );
    }, 200);
  };

  useEffect(() => {
    if (submitSucceeded && submittedClaim.claimObjectId && clientSettings.newUIFlag) {
      dispatch(getDashboardActions());
    }
    //Scroll to the top of the page
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [submitSucceeded]);

  return (
    <Fragment>
      <form onSubmit={handleSubmit}>
        <h3>
          <span className="claim-header">
            {props.intl.formatMessage(messages.claims.reviewClaimDescription)}
          </span>
        </h3>
        <div className="csp-side-menu claim-form-container">
          <div className="cxa-grid__item--nine">
            <Card>
              <CardSection>
                <h5>
                  <strong>{intl.formatMessage(messages.claims.claimDetailDescription)}</strong>
                </h5>
                <RenderField
                  key="planYear"
                  label={intl.formatMessage({
                    id: "claims.planYears",
                    defaultMessage: "Plan Year",
                  })}
                  value={claimDetail.benefitPeriod}
                  field={renderOutput}
                />
                {Array.isArray(claimItem.readOnlyFields) &&
                  claimItem.readOnlyFields.map(field => {
                    const fieldType = field.dataType;
                    const fieldValue = claimDetail[field.name];
                    const formattedValue = formatValue(
                      fieldValue,
                      fieldType,
                      claimDetail,
                      field,
                      intl
                    );

                    return (
                      <RenderField
                        id={field.name}
                        key={field.name}
                        label={intl.formatMessage({
                          id: field.label,
                          defaultMessage: field.label,
                        })}
                        value={formattedValue}
                        field={renderOutput}
                      />
                    );
                  })}
                {Array.isArray(claimItem.formFields) &&
                  claimItem.formFields
                    .filter(x => x.name != "receipt")
                    .map(field => {
                      const fieldType = field.dataType;
                      const fieldValue = claimDetail[field.fieldLowercased || field.name];
                      const formattedValue = formatValue(
                        fieldValue,
                        fieldType,
                        claimDetail,
                        field,
                        intl
                      );

                      return (
                        <RenderField
                          id={field.name}
                          key={field.name}
                          label={intl.formatMessage({
                            id: field.label,
                            defaultMessage: field.label,
                          })}
                          value={formattedValue}
                          field={renderOutput}
                        />
                      );
                    })}
              </CardSection>
              {claimDetail.attachments && (
                <Attachments attachments={claimDetail.attachments} {...props} />
              )}
              {claimItem.consentMessageTitle && (
                <h3
                  dangerouslySetInnerHTML={{
                    __html: intl.messages[claimItem.consentMessageTitle]
                      ? intl.messages[claimItem.consentMessageTitle]
                      : claimItem.consentMessageTitle,
                  }}
                />
              )}
              {claimItem.consentMessage && (
                <p>
                  <CheckboxField validate={[validateCheckRequired]} name="consent">
                    <div
                      dangerouslySetInnerHTML={{
                        __html: intl.messages[claimItem.consentMessage]
                          ? intl.messages[claimItem.consentMessage]
                          : claimItem.consentMessage,
                      }}
                    />
                  </CheckboxField>
                </p>
              )}
              <div className="claim-submit-button-container">
                <Button type="button" secondary onClick={goToPrevious}>
                  {intl.formatMessage(messages.editDescription)}
                </Button>
                <Button primary type="submit" submitting={submitting}>
                  {intl.formatMessage(messages.submitTextDescription)}
                </Button>
              </div>
            </Card>
          </div>
          <div className="cxa-grid__item--three  claim-panel-mobile-container">
            <ClaimRHS {...props} />
          </div>
        </div>
      </form>
      <Prompt
        when={dirty && !submitting}
        message={intl.formatMessage(messages.claims.saveDraftClaimInformationDescription, {
          expiredDate: addDays(numberOfDaysExpired),
        })}
      />
    </Fragment>
  );
};

PreviewClaimForm.propTypes = {
  intl: intlShape.isRequired,
  match: PropTypes.object.isRequired,
  push: PropTypes.func.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  submitting: PropTypes.bool,
  claimDetail: PropTypes.object,
  claimItem: PropTypes.object,
  submitSucceeded: PropTypes.bool,
};
const PreviewClaimPageForm = reduxForm({
  form: "submitClaim",
  onSubmit: submitClaim,
  enableReinitialize: true,
  keepDirtyOnReinitialize: true,
  forceUnregisterOnUnmount: true,
})(PreviewClaimForm);

const PreviewClaimPage = props => {
  const {
    match: { params },
  } = props;
  const dispatch = useDispatch();
  const claimItem = useSelector(state => state.claimsPP.claimItem);
  const claimDetail = useSelector(state => state.claimsPP.claimDetail);

  useEffect(() => {
    dispatch(getClaimDetail(params.claimId, params.selectedDelegator));
  }, []);

  const initialValues = {
    claimObjectId: params.claimId,
  };

  return (
    <Fragment>
      <ClaimStepper index={3} />
      {!claimItem || !claimDetail ? (
        <Loader />
      ) : (
        <PreviewClaimPageForm
          claimItem={claimItem}
          claimDetail={claimDetail}
          initialValues={initialValues}
          {...props}
        />
      )}
    </Fragment>
  );
};

export default withRouter(injectIntl(PreviewClaimPage));



// WEBPACK FOOTER //
// ./components/claim/PreviewClaimPage.js